import { FieldErrors } from 'react-hook-form';
import { ui } from '@procuros/datachecks';
import get from 'lodash/get';

import { DisplayModes } from 'services/webedi/types';

import {
  getAllWebEDIErrors,
  interpolateErrorVariables,
  parseErrorMessage,
  trackItemModificationGroupsError,
} from './utils';
import { remapErrorsToFirstCell } from './remapErrors';
import { WebEDIError } from './types';

type BuildFormErrorItemsArgs = {
  formErrors: FieldErrors;
  variables: Record<string, string>;
  uiConfig: Array<ui.Section> | undefined;
  displayMode: DisplayModes;
  dirtyFields: Record<string, unknown>;
  isBigMessage: boolean;
};

type BuildFormErrorItemsResult = {
  items: Array<WebEDIError>;
  hiddenErrors: Array<WebEDIError>;
  pendingFieldPaths: Array<string>;
};

export const buildFormErrorItems = ({
  formErrors,
  variables,
  uiConfig,
  displayMode,
  dirtyFields,
  isBigMessage,
}: BuildFormErrorItemsArgs): BuildFormErrorItemsResult => {
  const webEDIErrors = getAllWebEDIErrors(formErrors, variables);

  if (displayMode === DisplayModes.view) {
    Object.values(webEDIErrors).forEach(trackItemModificationGroupsError);
  }

  if (!webEDIErrors || !uiConfig) {
    return { items: [], hiddenErrors: [], pendingFieldPaths: [] };
  }

  const { remappedErrors, hiddenErrors } = remapErrorsToFirstCell(webEDIErrors, uiConfig);
  // No sort here: useNavigableAnnotations sorts the merged (form + annotation)
  // list before calling setItems. Sorting twice was pure waste.
  // The user cannot see or fix a hidden error, so it stays out of the banner count and navigation.
  const formErrorItems = Object.values(remappedErrors).filter((error) => error.type !== 'hidden');

  let pendingFieldPaths: Array<string> = [];
  if (isBigMessage && displayMode === DisplayModes.edit) {
    pendingFieldPaths = formErrorItems
      .filter((error) => Boolean(dirtyFields && get(dirtyFields, error.fieldPath)))
      .map((error) => error.fieldPath);
  }

  const hiddenDatachecksErrors: Array<WebEDIError> = [];
  const collectHiddenErrors = (obj: Record<string, unknown>, path = '') => {
    Object.entries(obj).forEach(([key, value]) => {
      const currentPath = path ? `${path}.${key}` : key;
      if (value && typeof value === 'object') {
        if ('type' in value && (value as { type: string }).type === 'hidden' && 'message' in value) {
          const error = parseErrorMessage((value as { message: unknown }).message);
          const { title, description } = interpolateErrorVariables(error, variables);
          hiddenDatachecksErrors.push({
            id: currentPath,
            fieldPath: currentPath,
            title,
            description,
            type: 'hidden',
            severity: 'error',
            isFormError: true,
          });
        } else if (!Array.isArray(value)) {
          collectHiddenErrors(value as Record<string, unknown>, currentPath);
        }
      }
    });
  };
  collectHiddenErrors(formErrors as Record<string, unknown>);

  return {
    items: formErrorItems,
    hiddenErrors: [...hiddenErrors, ...hiddenDatachecksErrors],
    pendingFieldPaths,
  };
};
